import { useState } from "react";
import { toast } from "sonner";
import { Seo, Eyebrow } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section } from "@/components/site/pageparts";
import { TextField, TextArea, SelectField, SubmitButton } from "@/components/site/forms";
import { HumanCheck } from "@/components/site/HumanCheck";
import { api } from "@/lib/api";
import { COMPANY } from "@/data/site";

const VALUES = [
  { t: "Evidence over opinion", d: "We publish the harness, not just the headline. A claim without a number is a hypothesis." },
  { t: "Ship the paper", d: "Research that never leaves the lab is a hobby. We productize what compounds." },
  { t: "Sovereignty is non-negotiable", d: "Local-first, owner-controlled, verifiable. We do not compromise the core guarantees." },
  { t: "Small teams, hard problems", d: "We stay small and pick problems that matter. Leverage over headcount." },
];

const ROLES = [
  { t: "Research Engineer — Retrieval Systems", m: "Push three-signal fusion past its current ceiling.", d: "Design and evaluate retrieval that fuses semantic, temporal and relational signals; own the harness.", s: "Strong IR/ML fundamentals; you reason about evaluation before models." },
  { t: "ML Engineer — On-device Inference", m: "Make sovereign memory fast on the hardware people own.", d: "Optimise local embeddings and reasoning; quantization, batching, and graceful degradation.", s: "You have shipped models that run offline, under real constraints." },
  { t: "Founding Product Engineer", m: "Turn a research substrate into a product people love.", d: "Full-stack ownership of the surfaces around the memory core — CLI, overlay, connectors.", s: "You ship end-to-end and care about craft." },
  { t: "Developer Advocate — UPII Open Source", m: "Grow the developers building on the substrate.", d: "Docs, examples, talks and community for the MIT-licensed core on PyPI.", s: "You write clearly and have built in the open." },
  { t: "Research Intern — Memory & Evaluation", m: "Help us measure what matters.", d: "Contribute to labelled sets, ablations and reproducibility of the evaluation.", s: "Rigorous, curious, and comfortable with uncertainty." },
];

export default function Careers() {
  const [loading, setLoading] = useState(false);
  const [human, setHuman] = useState({ valid: false, honeypot: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!human.valid) {
      toast.error("Please answer the spam check to continue.");
      return;
    }
    const f = e.target;
    setLoading(true);
    try {
      await api.post("/careers", {
        name: f.name.value,
        email: f.email.value,
        role: f.role.value,
        links: f.links.value,
        message: f.message.value,
        company_website: human.honeypot,
      });
      toast.success("Application received — thank you for your interest.");
      f.reset();
    } catch (err) {
      const s = err?.response?.status;
      const msg =
        s === 429
          ? "Too many submissions from this network. Please try again shortly."
          : s === 502
          ? "Our email provider is momentarily unavailable — please retry in a minute, or email careers@datafrontier.co."
          : "Something went wrong. Please email careers@datafrontier.co.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <Seo title="Careers" description="We hire people who think memory is the most important unsolved problem in AI." />
      <PageHero
        eyebrow="Company · Careers"
        title="Most labs are making models bigger. We're making them remember."
        lead="We hire people who think memory is the most important unsolved problem in AI. Bengaluru (HSR Layout) — remote-friendly for exceptional candidates."
      />

      <Section>
        <Reveal>
          <Eyebrow>Values</Eyebrow>
        </Reveal>
        <Stagger className="mt-10 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v) => (
            <Item key={v.t} className="bg-ink p-7">
              <h3 className="font-serif text-lg text-bone">{v.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-mist">{v.d}</p>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal>
          <Eyebrow>Open roles</Eyebrow>
          <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-5xl">Where you'd come in.</h2>
        </Reveal>
        <Stagger className="mt-12 divide-y divide-white/10 border-y border-white/10">
          {ROLES.map((r) => (
            <Item key={r.t}>
              <div className="grid gap-4 py-8 md:grid-cols-12 md:gap-8">
                <div className="md:col-span-5">
                  <h3 className="font-serif text-xl leading-snug text-bone">{r.t}</h3>
                  <p className="mt-2 text-sm italic text-saffron/90">{r.m}</p>
                </div>
                <p className="text-sm leading-relaxed text-mist md:col-span-4">{r.d}</p>
                <p className="text-sm leading-relaxed text-mist md:col-span-3">
                  <span className="font-mono text-[11px] uppercase tracking-wide text-mist/70">Signals</span>
                  <br />
                  {r.s}
                </p>
              </div>
            </Item>
          ))}
        </Stagger>
        <Reveal delay={0.1} className="mt-8 font-mono text-xs text-mist">
          Placeholder roles — [OPEN ROLES TO BE CONFIRMED]. Don't see your role? Tell us anyway.
        </Reveal>
      </Section>

      <Section className="border-t border-white/10">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Apply</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-4xl">Tell us why.</h2>
            <p className="mt-6 text-[15px] leading-relaxed text-mist">
              Or email <a href={`mailto:${COMPANY.emails.careers}`} className="link-underline pb-0.5 text-bone">{COMPANY.emails.careers}</a> directly.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2" data-testid="careers-form">
              <TextField id="name" label="Your name" name="name" required placeholder="Ada Lovelace" />
              <TextField id="email" label="Email" name="email" type="email" required placeholder="you@domain.com" />
              <div className="sm:col-span-2">
                <SelectField id="role" label="Role" name="role" options={ROLES.map((r) => r.t).concat(["Something else"])} />
              </div>
              <div className="sm:col-span-2">
                <TextField id="links" label="Links (GitHub, portfolio, PyPI)" name="links" placeholder="github.com/…" />
              </div>
              <div className="sm:col-span-2">
                <TextArea id="message" label="Why memory?" name="message" required placeholder="What draws you to this problem…" />
              </div>
              <div className="sm:col-span-2">
                <HumanCheck onChange={setHuman} />
              </div>
              <div className="sm:col-span-2">
                <SubmitButton loading={loading} disabled={!human.valid}>Send application</SubmitButton>
              </div>
            </form>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
